/**
 * Menu contents shared by the desktop fly-out and the mobile drawer: one Hub's
 * items (its own section, then "Also in <Hub>") or the user's pinned items.
 *
 * Every entry is marked `data-menu-item` so the parent can move focus with the
 * arrow keys. Coming-soon items are greyed, badged "Soon" and not links.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCircleInfo, faLock, faThumbtackSlash } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { DWA } from '../theme/dwaTokens';
import { findHub } from './hubs';
import type { Hub, HubItem } from './hubs';
import { hubSections, isAdminOnly, isComingSoon, itemNotes, pinnedItems } from './hubNav';
import { usePinnedItems } from './preferences';
import { Tooltip } from './Tooltip';

export type MenuVariant = 'flyout' | 'drawer';

const sectionLabel: React.CSSProperties = {
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: DWA.onDarkMuted,
  padding: '0.55rem 0.75rem 0.3rem',
  margin: 0,
};

const divider = <li role="separator" aria-hidden="true" style={{ height: 1, background: DWA.railLine, margin: '0.35rem 0.5rem' }} />;

export const SoonBadge: React.FC<{ tone?: 'dark' | 'light' }> = ({ tone = 'dark' }) => (
  <span
    style={{
      fontSize: '0.6rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      borderRadius: 999,
      padding: '0.1rem 0.45rem',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      ...(tone === 'dark'
        ? { color: DWA.gold, border: '1px solid rgba(254,195,92,0.45)', background: 'rgba(254,195,92,0.08)' }
        : { color: '#7a4b00', border: '1px solid #f8d79a', background: '#fff1d6' }),
    }}
  >
    Soon
  </span>
);

/** Tooltip body for an item: description, then notes ("Admin only", "Lives in Accounts"). */
export const ItemTip: React.FC<{ item: HubItem; hub?: Hub }> = ({ item, hub }) => {
  const notes = itemNotes(item, hub?.id);
  return (
    <>
      <span style={{ display: 'block' }}>{item.description ?? item.title}</span>
      {notes.length > 0 && (
        <span style={{ display: 'block', marginTop: '0.3rem', color: DWA.gold, fontWeight: 700, fontSize: '0.72rem' }}>
          {notes.join(' · ')}
        </span>
      )}
    </>
  );
};

const MenuEntry: React.FC<{
  item: HubItem;
  hub?: Hub;
  variant: MenuVariant;
  /** Show "from <home>" under the title. */
  showHome?: boolean;
  onNavigate: () => void;
  /** Pinned menu only: an unpin button beside the entry. */
  onUnpin?: () => void;
}> = ({ item, hub, variant, showHome, onNavigate, onUnpin }) => {
  const soon = isComingSoon(item);
  const lockShown = isAdminOnly(item) && hub?.id !== 'admin';
  const home = findHub(item.home);
  // The Soon badge already says "coming soon"; the drawer shows the other notes inline.
  const drawerNotes = itemNotes(item, hub?.id).filter((n) => n !== 'Coming soon');

  const inner = (
    <>
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0 }}>
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: variant === 'flyout' ? 'nowrap' : 'normal' }}>
          {item.title}
        </span>
        {soon && <SoonBadge />}
        {lockShown && (
          <FontAwesomeIcon icon={faLock} aria-label="Admin only" style={{ fontSize: '0.65rem', color: DWA.onDarkMuted, flexShrink: 0 }} />
        )}
        {variant === 'flyout' && (
          <FontAwesomeIcon icon={faCircleInfo} aria-hidden="true" style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
        )}
      </span>
      {showHome && home && (
        <span style={{ display: 'block', fontSize: '0.7rem', color: DWA.onDarkMuted, marginTop: '0.1rem' }}>from {home.title}</span>
      )}
      {variant === 'drawer' && item.description && (
        <span style={{ display: 'block', fontSize: '0.75rem', color: DWA.onDarkMuted, marginTop: '0.15rem', lineHeight: 1.4 }}>
          {item.description}
          {drawerNotes.length > 0 && <span style={{ color: DWA.gold }}> · {drawerNotes.join(' · ')}</span>}
        </span>
      )}
    </>
  );

  const style: React.CSSProperties = {
    display: 'block',
    padding: '0.45rem 0.75rem',
    borderRadius: DWA.radius,
    textDecoration: 'none',
    color: soon ? 'rgba(248,250,252,0.45)' : DWA.onDark,
    fontSize: '0.85rem',
    cursor: soon ? 'default' : 'pointer',
    fontFamily: DWA.font,
  };

  const el =
    soon || !item.route ? (
      <span data-menu-item tabIndex={-1} aria-disabled="true" className="ow-menu-item" style={style} data-testid={`menu-item-${item.id}`}>
        {inner}
      </span>
    ) : (
      <Link data-menu-item to={item.route} onClick={onNavigate} className="ow-menu-item" style={style} data-testid={`menu-item-${item.id}`}>
        {inner}
      </Link>
    );

  const entry = variant === 'flyout' ? <Tooltip content={<ItemTip item={item} hub={hub} />}>{el}</Tooltip> : el;
  if (!onUnpin) return <li>{entry}</li>;
  return (
    <li style={{ display: 'flex', alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>{entry}</div>
      <button
        type="button"
        data-menu-item
        className="ow-menu-item"
        aria-label={`Unpin ${item.title}`}
        title="Unpin"
        onClick={onUnpin}
        style={{
          flexShrink: 0,
          background: 'none',
          border: 'none',
          borderRadius: DWA.radius,
          padding: '0.45rem 0.6rem',
          color: DWA.onDarkMuted,
          cursor: 'pointer',
          fontSize: '0.8rem',
        }}
      >
        <FontAwesomeIcon icon={faThumbtackSlash} aria-hidden="true" />
      </button>
    </li>
  );
};

/** One Hub's menu: overview link, its own items, then "Also in <Hub>". */
export const HubMenu: React.FC<{ hub: Hub; variant: MenuVariant; onNavigate: () => void }> = ({ hub, variant, onNavigate }) => {
  const { user } = useAuth();
  const sections = hubSections(hub, user);
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid={`hub-menu-${hub.id}`}>
      <li>
        <Link
          data-menu-item
          to={hub.route}
          onClick={onNavigate}
          className="ow-menu-item"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.45rem 0.75rem',
            borderRadius: DWA.radius,
            color: DWA.gold,
            fontWeight: 700,
            fontSize: '0.85rem',
            textDecoration: 'none',
          }}
        >
          {hub.title} overview
          <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" style={{ fontSize: '0.7rem' }} />
        </Link>
      </li>
      {sections.map((section) => (
        <React.Fragment key={section.id}>
          {divider}
          <li role="presentation">
            <p style={sectionLabel} id={`menu-${variant}-${hub.id}-${section.id}`}>
              {section.id === 'own' ? 'In this Hub' : section.label}
            </p>
            <ul aria-labelledby={`menu-${variant}-${hub.id}-${section.id}`} style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid={`menu-section-${section.id}`}>
              {section.listings.map(({ item, isHome }) => (
                <MenuEntry key={item.id} item={item} hub={hub} variant={variant} showHome={!isHome} onNavigate={onNavigate} />
              ))}
            </ul>
          </li>
        </React.Fragment>
      ))}
    </ul>
  );
};

/** The user's pinned items (Home → Pinned), like HubSpot's Bookmarks. */
export const PinnedMenu: React.FC<{ variant: MenuVariant; onNavigate: () => void }> = ({ variant, onNavigate }) => {
  const { user } = useAuth();
  const { pins, togglePin } = usePinnedItems(user?.id);
  const items = pinnedItems(pins, user);
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="pinned-menu">
      {items.length === 0 ? (
        <li style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: DWA.onDarkMuted, lineHeight: 1.5 }}>
          Nothing pinned yet. Use the pin button on any Hub page, or Edit in Home → Quick links, to keep an item here.
        </li>
      ) : (
        items.map((item) => (
          <MenuEntry key={item.id} item={item} variant={variant} showHome onNavigate={onNavigate} onUnpin={() => togglePin(item.id)} />
        ))
      )}
    </ul>
  );
};
